import React, { useState, useRef, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parse, createSlug } from '../../util/urlHelpers';
import { getProductImageUrl } from './helpers';
import IconChevron from '../../components/IconChevron/IconChevron';
import NamedLink from '../../components/NamedLink/NamedLink';
import { SetBackupPiecesModal } from './SetBackupPiecesModal';
import { PiecesTable } from './PiecesTable';
import css from './ProductRow.css';

const ProductRow = ({ product, response, sdk, userPermissions, providers, currentProvider, onAddStock }) => {
  const { providerId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const accordionRef = useRef(null);
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);
  const [apiError, setApiError] = useState(null);

  const rawId = product?.id?.uuid || product?.id;
  const listingId = { uuid: rawId };
  const catalogItemId = product?.relationships?.catalogItem?.data?.id?.uuid;

  const managedPiecesEnabled = product?.attributes?.managedPiecesEnabled ?? false;
  const state = product?.attributes?.state;
  const title = product?.attributes?.title || '';
  const slug = createSlug(title);
  const hasCatalogItem = !!catalogItemId;

  const managedPieces = product?.attributes?.managedPiecesData?.managed ?? 0;
  const backupPiecesCount = product?.attributes?.managedPiecesData?.backup ?? 0;

  const imageUrl = getProductImageUrl(product, response);

  // --- ZARZĄDZANIE STANEM ROZWINIĘCIA PRZEZ URL ---
  const queryParams = parse(location.search);
  const isExpanded = queryParams.expanded === rawId;

  useEffect(() => {
    if (isExpanded && accordionRef.current) {
      const timer = setTimeout(() => {
        if (accordionRef.current) {
          accordionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Mutacje otwarcia / zamknięcia oferty
  const { mutate: handleToggleListingState, isLoading: isStateToggling } = useMutation({
    mutationFn: async (action) => {
      setApiError(null);
      const listingsApi = sdk?.newSdk?.listings || sdk?.listings;

      if (!listingsApi) {
        throw new Error('Brak dostępnego SDK dla ofert.');
      }

      if (action === 'open') {
        return listingsApi.open({ id: listingId.uuid });
      }
      if (action === 'close') {
        return listingsApi.close({ id: listingId.uuid });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ownListings']);
      queryClient.invalidateQueries(['manageListings']);
    },
    onError: (err) => {
      console.error('Error toggling listing state:', err);
      setApiError('Nie udało się zmienić stanu oferty.');
    },
  });

  const handleHeaderClick = () => {
    if (!hasCatalogItem) return;

    const currentScrollY = window.scrollY;
    const newQueryParams = { ...queryParams };

    if (isExpanded) {
      delete newQueryParams.expanded;
    } else {
      newQueryParams.expanded = rawId;
    }

    const searchString = Object.keys(newQueryParams)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(newQueryParams[key])}`)
      .join('&');

    history.replace({
      pathname: location.pathname,
      search: searchString ? `?${searchString}` : '',
    });

    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const handleOpenBackupModal = (e) => {
    e.stopPropagation();
    setIsBackupModalOpen(true);
  };

  const handleAddFirstPiece = () => {
    if (typeof onAddStock === 'function') {
      onAddStock(product);
    }
  };

  const handleToggleStateClick = (e, action) => {
    e.stopPropagation();
    handleToggleListingState(action);
  };

  const handleGoToSeasonality = (e) => {
    e.stopPropagation();
    history.push(`/stock/${currentStoreId}/availability/${rawId}`);
  };

  const isClosed = state === 'closed';
  const isPublished = state === 'published';

  const accordionClassName = `${css.productAccordion} ${isClosed ? css.closedRow : ''}`;
  const headerClassName = `${css.productHeader} ${hasCatalogItem ? css.clickableHeader : css.disabledHeader}`;

  const currentStoreId = providerId || currentProvider?.id?.uuid || currentProvider?.id;

  return (
    <div ref={accordionRef} className={accordionClassName}>
      <div className={headerClassName} onClick={handleHeaderClick}>
        <div className={css.productInfo}>
          {imageUrl ? (
            <img src={imageUrl} alt={title} className={css.productImage} />
          ) : (
            <span className={css.bikeIcon}>📦</span>
          )}
          <div className={css.productMetaWrapper}>
            <div className={css.titleRow}>
              <h3 className={css.productName}>{title}</h3>
              {isClosed && <span className={css.closedBadge}>Zamknięta</span>}
            </div>

            {managedPiecesEnabled ? (
              <div className={css.backupSubtitleRow}>
                <div className={css.piecesCountGroup}>
                  <span>
                    Łącznie: {managedPieces} (w tym {backupPiecesCount} rezerwowe)
                  </span>
                  <button
                    type="button"
                    className={css.editPencilBtn}
                    onClick={handleOpenBackupModal}
                    title="Edytuj liczbę sztuk rezerwowych"
                  >
                    ✏️
                  </button>
                </div>

                <div onClick={(e) => e.stopPropagation()}>
                  <NamedLink
                    name="ManageStockSeasonalityPage"
                    params={{ listingId: rawId }}
                    className={css.seasonalitySubBtn}
                  >
                    Zarządzaj sezonowością
                  </NamedLink>
                </div>
              </div>
            ) : (
              <div className={css.untrackedSubtitleRow}>
                <span className={css.untrackedBadge}>Brak śledzenia sztuk</span>
              </div>
            )}
          </div>
        </div>

        <div className={css.headerActions}>
          {!managedPiecesEnabled && (
            <>
              {isPublished && (
                <button
                  type="button"
                  className={css.stateToggleHeaderBtn}
                  disabled={isStateToggling}
                  onClick={(e) => handleToggleStateClick(e, 'close')}
                >
                  {isStateToggling ? 'Zamykanie...' : 'Zamknij ofertę'}
                </button>
              )}

              {isClosed && (
                <button
                  type="button"
                  className={css.stateToggleHeaderBtnOpen}
                  disabled={isStateToggling}
                  onClick={(e) => handleToggleStateClick(e, 'open')}
                >
                  {isStateToggling ? 'Otwieranie...' : 'Otwórz ofertę'}
                </button>
              )}
            </>
          )}

          {hasCatalogItem ? (
            <div className={`${css.arrow} ${isExpanded ? css.arrowOpen : ''}`}>
              <IconChevron />
            </div>
          ) : (
            <span className={css.noCatalogBadge}>Spoza katalogu</span>
          )}
        </div>
      </div>

      {apiError && <div className={css.inlineHeaderError}>{apiError}</div>}

      {isExpanded && hasCatalogItem && (
        managedPiecesEnabled ? (
          <PiecesTable
            listingId={rawId}
            sdk={sdk}
            userPermissions={userPermissions}
            providers={providers}
            currentProvider={currentProvider}
            onAddStock={onAddStock}
          />
        ) : (
          <div className={css.legacyExpandedPanel}>
            <div className={css.legacyInfoBox}>
              <div className={css.legacyTitle}>Tradycyjne zarządzanie dostępnością</div>
              <div className={css.legacyDescription}>
                Ten produkt nie ma obecnie przypisanych sztuk fizycznych. Dostępność jest zarządzana na podstawie grafiku kalendarza.
              </div>
            </div>

            <div className={css.legacyActionsRow}>
              <button
                type="button"
                className={css.addFirstPieceBtn}
                onClick={handleAddFirstPiece}
              >
                + Dodaj pierwszą sztukę
              </button>

              <div className={css.legacyLinksGroup}>
                <NamedLink
                  className={css.manageLink}
                  name="EditListingPage"
                  params={{ id: rawId, slug, type: 'edit', tab: 'availability' }}
                >
                  Zarządzaj grafikiem
                </NamedLink>
              </div>
            </div>
          </div>
        )
      )}

      {managedPiecesEnabled && (
        <SetBackupPiecesModal
          open={isBackupModalOpen}
          onOpenChange={setIsBackupModalOpen}
          product={product}
          totalActivePieces={managedPieces}
          sdk={sdk}
        />
      )}
    </div>
  );
};

export default ProductRow;